/**
 * MetricCard Component - T140
 *
 * Card displaying a single metric with optional trend indicator
 */
import { GlassPanel } from '../ui';
import { formatCurrency } from '@armard/shared';

interface MetricCardProps {
  title: string;
  value: number;
  format?: 'currency' | 'number' | 'percent';
  trend?: {
    value: number;
    label: string;
  };
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

export function MetricCard({
  title,
  value,
  format = 'number',
  trend,
  icon,
  className = '',
  onClick,
}: MetricCardProps) {
  const formatValue = (val: number) => {
    switch (format) {
      case 'currency':
        return formatCurrency(val);
      case 'percent':
        return `${val.toFixed(1)}%`;
      default:
        return val.toLocaleString('de-DE');
    }
  };

  const trendIsPositive = trend && trend.value > 0;
  const trendIsNegative = trend && trend.value < 0;

  return (
    <GlassPanel
      className={`${onClick ? 'cursor-pointer hover:bg-white/10 transition-colors' : ''} ${className}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm text-white/50 mb-2">{title}</p>
          <p className="text-2xl font-semibold text-white">{formatValue(value)}</p>

          {trend && (
            <div className="flex items-center gap-1 mt-2">
              {trendIsPositive && (
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              )}
              {trendIsNegative && (
                <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              )}
              <span
                className={`text-xs ${
                  trendIsPositive ? 'text-green-400' : trendIsNegative ? 'text-red-400' : 'text-white/40'
                }`}
              >
                {trend.value > 0 ? '+' : ''}
                {formatValue(Math.abs(trend.value))} {trend.label}
              </span>
            </div>
          )}
        </div>

        {icon && <div className="p-2 bg-white/5 rounded-lg text-white/40">{icon}</div>}
      </div>
    </GlassPanel>
  );
}
